import { Col, Row, Typography } from "antd";
import React, { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { Booking } from "../../../../../../types/bookings";
import { InputCheckbox, InputField } from "../../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../atoms/FormElement/InputNumberField";
const parseNumber = (value: string | number | undefined | null): number => (isNaN(Number(value)) ? 0 : Number(value));

type CalculateRequiredTopUpAmountOptions = {
  renewalAmount: number;
  percentage: number;
  totalPaid: number;
};

const calculateRequiredTopUpAmount = (options: CalculateRequiredTopUpAmountOptions) => {
  const { renewalAmount, percentage, totalPaid } = options;
  const requiredDeposit = (renewalAmount * percentage) / 100;
  return Math.max(requiredDeposit - totalPaid, 0).toFixed(0);
};

type CalculateCurrentPercentageOptions = {
  renewalAmount: number;
  totalPaid: number;
};

const calculateCurrentPercentage = (options: CalculateCurrentPercentageOptions) => {
  const { renewalAmount, totalPaid } = options;
  if (!renewalAmount || renewalAmount === 0) return 0;
  return Number(((totalPaid / renewalAmount) * 100).toFixed(1));
};

export const SecurityDepositTopUp = ({ disabled = false }: { disabled?: boolean }) => {
  const booking = useSelector((state: any) => state.newBooking);
  const bookingDetails = booking?.bookingDetails as Booking;

  const { bookingTerms, totalSecurityDepositPaid, securityDepositPaidPercentageLimit } = bookingDetails;
  const { annualBaseRent, renewalAmount, securityDepositTopUp } = bookingTerms;

  const totalPaid = parseNumber(totalSecurityDepositPaid);
  const defaultPercentage = parseNumber(securityDepositTopUp?.requiredPercentage ?? securityDepositPaidPercentageLimit);

  const { control, setValue } = useFormContext();

  const watchedIsTopUpRequired = useWatch({ name: "isTopUpRequired", control });
  const watchedRequiredPercentage = useWatch({ name: "securityDepositTopUp.requiredPercentage", control });
  const watchedRenewalAmount = useWatch({ name: "renewalAmount", control });

  useEffect(() => {
    setValue("securityDepositTopUp.currentAmount", totalPaid);
    setValue("securityDepositTopUp.requiredAmount", 0);
    setValue("securityDepositTopUp.requiredPercentage", 0);

    if (securityDepositTopUp != null) setValue("isTopUpRequired", true);
  }, [setValue, totalPaid, securityDepositTopUp]);

  const effectiveRenewalAmount = useMemo(() => {
    return parseNumber(watchedRenewalAmount || renewalAmount || annualBaseRent);
  }, [watchedRenewalAmount, renewalAmount, annualBaseRent]);

  useEffect(() => {
    if (!watchedIsTopUpRequired) {
      setValue("securityDepositTopUp.requiredPercentage", 0);
      setValue("securityDepositTopUp.requiredAmount", 0);
      return;
    }

    const requiredAmount = calculateRequiredTopUpAmount({
      renewalAmount: effectiveRenewalAmount,
      percentage: defaultPercentage,
      totalPaid,
    });

    setValue("securityDepositTopUp.requiredAmount", requiredAmount);
    setValue("securityDepositTopUp.requiredPercentage", defaultPercentage);
  }, [watchedIsTopUpRequired, effectiveRenewalAmount, defaultPercentage, totalPaid, setValue]);

  useEffect(() => {
    if (!watchedIsTopUpRequired) return;

    const requiredAmount = calculateRequiredTopUpAmount({
      renewalAmount: effectiveRenewalAmount,
      percentage: watchedRequiredPercentage,
      totalPaid,
    });

    setValue("securityDepositTopUp.requiredAmount", requiredAmount);
  }, [watchedRequiredPercentage, effectiveRenewalAmount, totalPaid, watchedIsTopUpRequired]);

  const currentPercentage = calculateCurrentPercentage({ renewalAmount: effectiveRenewalAmount, totalPaid });

  return (
    <>
      <Row gutter={24}>
        <Col>
          <Typography.Title level={5}>Security Deposit Top-Up</Typography.Title>
        </Col>
        <Col flex="auto">
          <InputCheckbox
            control={control}
            fieldname="isTopUpRequired"
            label="Required"
            iProps={{ className: "ds-checkbox", disabled }}
          />
        </Col>
      </Row>
      <Row gutter={12}>
        <Col flex="1">
          <InputNumberField
            disabled
            control={control}
            fieldname="securityDepositTopUp.currentAmount"
            label="Current Deposit Amount (AED)"
            format="currency"
            defaultValue={totalPaid}
            formItemProps={{ extra: `${currentPercentage}% of annual rent` }}
          />
        </Col>
        <Col flex="1">
          <InputNumberField
            disabled={disabled || !watchedIsTopUpRequired}
            control={control}
            fieldname="securityDepositTopUp.requiredPercentage"
            defaultValue={0}
            label="Required Deposit (%)"
            inputNumberProps={{
              min: 0,
              max: 100,
              precision: 0,
            }}
          />
        </Col>
        <Col flex="1">
          <InputNumberField
            disabled
            control={control}
            fieldname="securityDepositTopUp.requiredAmount"
            label="Top-Up Amount (AED)"
            format="currency"
            defaultValue={0}
          />
          
        </Col>
      </Row>
    </>
  );
};
